import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Upload, FileText, ShieldCheck, PenLine, Eye } from "lucide-react";
import { api, formatApiError, openFile } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const FORMS = [
  {
    key: "intake_information",
    title: "Intake Information Form",
    desc: "Your personal and filing information — the starting point for every return.",
    file: "/forms/intake-information.pdf",
    pages: "2 pages",
    fields: [
      { name: "full_name", label: "Full legal name", required: true },
      { name: "dob", label: "Date of birth", type: "date" },
      { name: "ssn_last4", label: "SSN (last 4)", maxLength: 4 },
      { name: "phone", label: "Phone", type: "tel" },
      { name: "email", label: "Email", type: "email" },
      { name: "street", label: "Street address" },
      { name: "city", label: "City" },
      { name: "state", label: "State" },
      { name: "zip", label: "ZIP" },
      { name: "filing_status", label: "Filing status", type: "select", options: ["Single", "Married Filing Jointly", "Married Filing Separately", "Head of Household", "Qualifying Widow(er)"] },
      { name: "occupation", label: "Occupation" },
      { name: "spouse_name", label: "Spouse name (if filing jointly)" },
      { name: "dependents", label: "Dependents (name, relationship, DOB — one per line)", type: "textarea" },
      { name: "notes", label: "Anything we should know?", type: "textarea" },
    ],
  },
  {
    key: "schedule_c",
    title: "Schedule C Tax Information",
    desc: "Business income, expenses, and mileage details for self-employed clients.",
    file: "/forms/schedule-c-tax-information.pdf",
    pages: "2 pages",
    fields: [
      { name: "business_name", label: "Business name", required: true },
      { name: "federal_id", label: "Federal ID # (if any)" },
      { name: "business_address", label: "Business address" },
      { name: "business_activity", label: "Main business activity" },
      { name: "gross_income", label: "Total gross business income" },
      { name: "filed_1099", label: "Did you file required Form 1099?", type: "select", options: ["Yes", "No", "Not applicable"] },
      { name: "advertising", label: "Advertising expense" },
      { name: "supplies", label: "Supplies expense" },
      { name: "rent", label: "Rent / lease expense" },
      { name: "mileage", label: "Business miles driven" },
      { name: "mileage_evidence", label: "Do you have evidence to support your mileage?", type: "select", options: ["Yes", "No"] },
      { name: "mileage_evidence_written", label: "If yes, is the evidence written?", type: "select", options: ["Yes", "No", "Not applicable"] },
      { name: "other_expenses", label: "Other expenses (describe with amounts)", type: "textarea" },
      { name: "notes", label: "Additional notes", type: "textarea" },
    ],
  },
  {
    key: "bank_account",
    title: "Bank Account Information",
    desc: "Direct deposit authorization so your refund goes straight to your account.",
    file: "/forms/bank-account-information.pdf",
    pages: "1 page",
    fields: [
      { name: "full_name", label: "Full legal name", required: true },
      { name: "address", label: "Address" },
      { name: "bank_name", label: "Bank name", required: true },
      { name: "routing_number", label: "Routing number", required: true, maxLength: 9 },
      { name: "account_number", label: "Account number", required: true },
      { name: "account_type", label: "Account type", type: "select", options: ["Checking", "Savings"] },
      { name: "authorize", label: "I authorize Laronta Pro Tax Services LLC to deposit my tax refund into the account above.", type: "checkbox", required: true },
    ],
  },
];

export default function FormsCenter() {
  const [formKey, setFormKey] = useState("intake_information");
  const [completed, setCompleted] = useState([]);
  const [onlineForms, setOnlineForms] = useState([]);
  const [fillForm, setFillForm] = useState(null);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [viewEntry, setViewEntry] = useState(null);
  const fileRef = useRef(null);

  const load = () => {
    api.get("/documents").then((r) => setCompleted(r.data.filter((d) => d.category === "completed_form"))).catch(() => {});
    api.get("/completed-forms").then((r) => setOnlineForms(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const openFill = (form) => {
    setFormData({});
    setFillForm(form);
  };

  const setField = (name, value) => setFormData((d) => ({ ...d, [name]: value }));

  const submitOnline = async () => {
    for (const f of fillForm.fields) {
      if (f.required && !formData[f.name]) {
        toast.error(`Please complete: ${f.label}`);
        return;
      }
    }
    setSubmitting(true);
    try {
      await api.post("/completed-forms", { form_key: fillForm.key, data: formData });
      toast.success(`${fillForm.title} submitted — our team can now review it`);
      setFillForm(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Submission failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const uploadCompleted = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", "completed_form");
    fd.append("form_name", FORMS.find((f) => f.key === formKey)?.title || "");
    try {
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} uploaded — our team can now review it`);
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Upload failed"));
    } finally {
      e.target.value = "";
    }
  };

  return (
    <div data-testid="document-center-page" className="space-y-8 animate-fade-up max-w-3xl">
      <div>
        <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-medium">Client portal</p>
        <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tight mt-1">Document Center</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-xl">
          For each form you can either fill it out directly here in the portal, or download the PDF, fill it by hand, and upload the completed copy back.
        </p>
      </div>

      <div className="space-y-4" data-testid="blank-forms-list">
        {FORMS.map((f) => (
          <Card key={f.key} data-testid={`form-card-${f.key}`} className="hover:-translate-y-0.5 hover:shadow-md transition-transform duration-200">
            <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="w-11 h-11 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                  <FileText className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{f.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{f.desc} · PDF · {f.pages}</p>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" onClick={() => openFill(f)} data-testid={`fill-online-${f.key}`}>
                  <PenLine className="w-4 h-4 mr-1" /> Fill Online
                </Button>
                <Button asChild variant="outline" size="sm">
                  <a href={f.file} download data-testid={`form-download-${f.key}`}>
                    <Download className="w-4 h-4 mr-1" /> Download PDF
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card data-testid="completed-upload-card">
        <CardHeader>
          <CardTitle className="font-heading text-xl flex items-center gap-2">
            <Upload className="w-5 h-5 text-primary" /> Upload a Hand-Filled Form
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3 items-end">
            <div className="space-y-1.5 flex-1">
              <Label>Which form is this?</Label>
              <Select value={formKey} onValueChange={setFormKey}>
                <SelectTrigger data-testid="completed-form-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FORMS.map((f) => <SelectItem key={f.key} value={f.key} data-testid={`completed-form-option-${f.key}`}>{f.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button type="button" onClick={() => fileRef.current?.click()} data-testid="completed-upload-button">
              <Upload className="w-4 h-4 mr-1" /> Choose File
            </Button>
            <input ref={fileRef} type="file" className="hidden" data-testid="completed-file-input"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp,.heic" onChange={uploadCompleted} />
          </div>
          <p className="text-xs text-muted-foreground flex items-start gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0 text-primary" />
            Completed forms — online or uploaded — are visible only to you, the Admin, and your assigned team member. Scans or clear photos (JPG/PNG) and PDF/Word files accepted, max 15MB.
          </p>
        </CardContent>
      </Card>

      <Card data-testid="my-completed-forms-card">
        <CardHeader><CardTitle className="font-heading text-xl">My Completed Forms</CardTitle></CardHeader>
        <CardContent className="space-y-2" data-testid="my-completed-forms-list">
          {onlineForms.length === 0 && completed.length === 0 && (
            <p className="text-sm text-muted-foreground">No completed forms yet.</p>
          )}
          {onlineForms.map((f) => (
            <div key={f.entry_id} className="flex items-center gap-3 border border-border rounded-md p-3" data-testid={`online-form-row-${f.entry_id}`}>
              <PenLine className="w-4 h-4 text-primary" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{f.form_title}</p>
                <p className="text-xs text-muted-foreground">Filled online · submitted {f.submitted_at?.slice(0, 10)}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => setViewEntry(f)} data-testid={`online-form-view-${f.entry_id}`}>
                <Eye className="w-3.5 h-3.5 mr-1" /> View
              </Button>
            </div>
          ))}
          {completed.map((d) => (
            <div key={d.file_id} className="flex items-center gap-3 border border-border rounded-md p-3" data-testid={`completed-form-row-${d.file_id}`}>
              <FileText className="w-4 h-4 text-primary" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{d.original_filename}</p>
                <p className="text-xs text-muted-foreground">{d.form_name || "Completed form"} · uploaded {d.created_at?.slice(0, 10)}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => openFile(d.file_id)} data-testid={`completed-form-open-${d.file_id}`}>Open</Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <Dialog open={!!fillForm} onOpenChange={(o) => !o && setFillForm(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="online-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{fillForm?.title}</DialogTitle>
            <DialogDescription>Fill this form directly in the portal. It will be visible only to you, the Admin, and your assigned team member.</DialogDescription>
          </DialogHeader>
          {fillForm && (
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {fillForm.fields.map((f) => {
                  if (f.type === "textarea") {
                    return (
                      <div key={f.name} className="space-y-1.5 sm:col-span-2">
                        <Label>{f.label}{f.required ? " *" : ""}</Label>
                        <Textarea rows={3} value={formData[f.name] || ""} onChange={(e) => setField(f.name, e.target.value)} data-testid={`online-field-${f.name}`} />
                      </div>
                    );
                  }
                  if (f.type === "select") {
                    return (
                      <div key={f.name} className="space-y-1.5">
                        <Label>{f.label}{f.required ? " *" : ""}</Label>
                        <Select value={formData[f.name] || ""} onValueChange={(v) => setField(f.name, v)}>
                          <SelectTrigger data-testid={`online-field-${f.name}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                          <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    );
                  }
                  if (f.type === "checkbox") {
                    return (
                      <label key={f.name} className="flex items-start gap-3 cursor-pointer sm:col-span-2" data-testid={`online-field-${f.name}-label`}>
                        <Checkbox checked={!!formData[f.name]} onCheckedChange={(v) => setField(f.name, !!v)} data-testid={`online-field-${f.name}`} />
                        <span className="text-sm text-muted-foreground">{f.label}{f.required ? " *" : ""}</span>
                      </label>
                    );
                  }
                  return (
                    <div key={f.name} className="space-y-1.5">
                      <Label>{f.label}{f.required ? " *" : ""}</Label>
                      <Input type={f.type || "text"} maxLength={f.maxLength} value={formData[f.name] || ""} onChange={(e) => setField(f.name, e.target.value)} data-testid={`online-field-${f.name}`} />
                    </div>
                  );
                })}
              </div>
              <Button className="w-full" onClick={submitOnline} disabled={submitting} data-testid="online-submit-button">
                {submitting ? "Submitting…" : "Submit Form"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewEntry} onOpenChange={(o) => !o && setViewEntry(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="view-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{viewEntry?.form_title}</DialogTitle>
            <DialogDescription>Submitted {viewEntry?.submitted_at?.slice(0, 10)}</DialogDescription>
          </DialogHeader>
          <dl className="space-y-2 pt-2">
            {Object.entries(viewEntry?.data || {}).map(([k, v]) => (
              <div key={k} className="flex gap-3 text-sm border-b border-border pb-2 last:border-0">
                <dt className="text-muted-foreground capitalize w-40 shrink-0">{k.replace(/_/g, " ")}</dt>
                <dd className="break-words whitespace-pre-wrap">{typeof v === "boolean" ? (v ? "Yes" : "No") : String(v)}</dd>
              </div>
            ))}
          </dl>
        </DialogContent>
      </Dialog>
    </div>
  );
}
